import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import NotificationSounds, {
  Sound,
  playSampleSound,
  stopSampleSound,
} from 'react-native-notification-sounds';

interface useSystemSoundsState {
  status: 'loading' | 'ready';
  sounds: Sound[];
}

interface useSystemSoundReturn extends useSystemSoundsState {
  play: (s: Sound) => void;
  playByID: (id?: string) => void;
  stop: () => void;
}

const isWeb = Platform.OS === 'web';

const useSystemSounds = (props: {
  type: 'alarm' | 'notification';
}): useSystemSoundReturn => {
  const [state, setState] = useState<useSystemSoundsState>({
    status: 'loading',
    sounds: [],
  });

  /** Plays sound by its ID. Not for web though */
  const playByID = useCallback(
    (id?: string) => {
      if (!isWeb) {
        const sampleSound =
          state.sounds.find((sound) => sound.soundID === id) || state.sounds[0];
        if (sampleSound) {
          playSampleSound(sampleSound);
        }
      }
    },
    [state.sounds]
  );

  const stop = useCallback(() => !isWeb && stopSampleSound(), []);

  const play = useCallback(
    (sound: Sound) => !isWeb && playSampleSound(sound),
    []
  );

  useEffect(() => {
    if (!isWeb) {
      NotificationSounds.getNotifications(props.type).then((sounds) => {
        setState({
          status: 'ready',
          sounds: sounds.map((s) => ({ ...s, id: s.soundID })),
        });
      });
    }
  }, [props.type]);

  return { ...state, play, playByID, stop };
};

export const useNotificationSounds = (): useSystemSoundReturn =>
  useSystemSounds({ type: 'notification' });

export const useAlarmSounds = (): useSystemSoundReturn =>
  useSystemSounds({ type: 'alarm' });
